import type { ChangeEvent, ComponentProps, ReactNode, Ref } from 'react';
import { T } from 'gt-react';
import { useFileDrop } from '@/hooks/useFileDrop';
import { cn } from '@/lib/utils';
import { ChatInput } from './ChatInput';

/**
 * Drop target for assistant files.
 *
 * A file drop the page does not handle navigates the browser to that file
 * (a new tab, and the conversation is gone). This surface accepts the drop,
 * and while it is mounted a drop that misses it is cancelled instead.
 */
export function ChatFileDropSurface({
  onDropFiles,
  className,
  children,
}: {
  onDropFiles: (files: File[]) => void;
  className?: string;
  children: ReactNode;
}) {
  const { isDragActive, dropTargetProps } = useFileDrop({ onDropFiles });

  return (
    <div
      data-testid="chat-file-drop"
      className={cn('relative', className)}
      {...dropTargetProps}
    >
      {children}
      {isDragActive && (
        <div
          className="border-primary bg-background/80 pointer-events-none absolute inset-0 z-20 flex items-center justify-center border-2 border-dashed p-6"
          aria-hidden
        >
          <p className="bg-background text-foreground rounded-md px-3 py-2 text-sm font-medium shadow-sm">
            <T context="overlay shown while files are dragged over the AI assistant">
              Drop files to attach them
            </T>
          </p>
        </div>
      )}
    </div>
  );
}

/** Hidden picker the paperclip button opens. Same files as a drop. */
export function ChatFileInput({
  inputRef,
  onChange,
}: {
  inputRef: Ref<HTMLInputElement>;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void | Promise<void>;
}) {
  return (
    <input
      ref={inputRef}
      type="file"
      multiple
      className="hidden"
      onChange={(event) => {
        void onChange(event);
      }}
    />
  );
}

type ComposerFiles = {
  pending: ComponentProps<typeof ChatInput>['attachments'];
  uploading: boolean;
  fileInputRef: Ref<HTMLInputElement>;
  onFileInputChange: (
    event: ChangeEvent<HTMLInputElement>,
  ) => void | Promise<void>;
  openFilePicker: () => void;
  remove: (documentId: string) => void;
};

/** Paperclip, hidden picker, and staged files. Shared by the Home composers. */
export function AssistantFileComposer({
  files,
  ...inputProps
}: { files: ComposerFiles } & Omit<
  ComponentProps<typeof ChatInput>,
  'attachments' | 'onAttachFile' | 'onRemoveAttachment' | 'sendDisabled'
>) {
  return (
    <>
      <ChatFileInput
        inputRef={files.fileInputRef}
        onChange={files.onFileInputChange}
      />
      <ChatInput
        {...inputProps}
        attachments={files.pending}
        onAttachFile={files.openFilePicker}
        onRemoveAttachment={files.remove}
        sendDisabled={files.uploading}
      />
    </>
  );
}
